import classNames from 'classnames';
import { useMemo, useState } from 'react';
import { defineMessages } from 'react-intl';
import { useFormatMessage } from '@alltrails/core/hooks/useFormatMessage';
import Chevron from '../../icons/Chevron';
import Close from '../../icons/Close';
import setDenaliAttribute from '../../utils/setDenaliAttribute';
import IconButton from '../IconButton';
import IconRenderer, { IconDefinition } from '../IconRenderer';
import styles from './styles/styles.module.scss';

const messages = defineMessages({
  CLOSE: { defaultMessage: 'Close' },
  SHOW_MORE: { defaultMessage: 'Show more' },
  SHOW_LESS: { defaultMessage: 'Show less' }
});

export type SectionMessageProps = {
  className?: string;
  expandedText?: React.ReactNode;
  icon?: IconDefinition<'color' | 'orientation'>;
  onDismiss?: () => void;
  text: React.ReactNode;
  title?: React.ReactNode;
  variant?: 'caution' | 'error' | 'info' | 'neutral' | 'success';
};

const SectionMessage = ({ className, expandedText = null, icon, onDismiss, text, title, variant = 'neutral' }: SectionMessageProps) => {
  const {
    formattedDefaultMessages: { CLOSE, SHOW_MORE, SHOW_LESS }
  } = useFormatMessage(messages);

  const [isExpanded, setIsExpanded] = useState(false);

  const iconButton = useMemo(() => {
    if (expandedText) {
      return (
        <IconButton
          className={styles.iconButton}
          icon={{ Component: Chevron, orientation: isExpanded ? 'up' : 'down' }}
          size="sm"
          onClick={() => setIsExpanded(!isExpanded)}
          title={isExpanded ? SHOW_LESS : SHOW_MORE}
          variant="ghost"
        />
      );
    }
    if (onDismiss) {
      return <IconButton icon={{ Component: Close }} size="sm" onClick={onDismiss} title={CLOSE} variant="ghost" />;
    }
    return null;
  }, [CLOSE, SHOW_LESS, SHOW_MORE, expandedText, isExpanded, onDismiss]);

  return (
    <div {...setDenaliAttribute('SectionMessage')} className={classNames(styles.sectionMessage, styles[variant], className)}>
      {icon ? (
        <div className={styles.iconContainer}>
          <IconRenderer icon={icon} defaults={{ color: 'currentColor', size: 'sm' }} />
        </div>
      ) : null}
      <div className={styles.content}>
        <div className={styles.persistentContent}>
          <div className={styles.persistentText}>
            {title && <div className={styles.title}>{title}</div>}
            <div className={styles.text}>{text}</div>
          </div>
          {iconButton}
        </div>
        {expandedText && isExpanded && <div className={styles.expandedText}>{expandedText}</div>}
      </div>
    </div>
  );
};

export default SectionMessage;
